/**
 * PANDA: Such-Panel für die Header-Suche.
 *
 * Ersetzt die Stock-Live-Suche aus theme.js, ohne theme.js anzufassen: deren
 * Handler hängt delegiert an `.search-bar[data-live-search="true"]`, deshalb
 * setzt dieses Skript das Attribut auf "panda" und übernimmt selbst.
 *
 * - Startzustand beim Reinklicken: Zuletzt gesucht, Beliebte Suchen, Bestseller
 * - ab 2 Zeichen: Vorschläge, Kategorien, Produkte mit Bild und Preis
 * - Autovervollständigung als graue Ergänzung im Feld (Tab oder → übernimmt)
 * - Vorschau: Maus oder Pfeiltasten auf einem Vorschlag zeigen dessen Produkte
 * - Tastatur: ↑/↓ wählen, Enter öffnet, Esc schließt; „/“ springt ins Suchfeld
 * - Motiv-Kacheln für Motiv-Kollektionen (rendert die Section)
 * - Suchseite: das große Suchfeld ([data-panda-search]) nutzt dasselbe Panel,
 *   „Ähnliche Suchen“ und „Passende Kategorien“ füllt fillRelated()
 *
 * Inhalt rendert sections/panda-predictive-search.liquid über die Section
 * Rendering API; hier wird nur geholt, eingesetzt und gesteuert.
 */
(function () {
  'use strict';

  var SECTION_ID = 'panda-predictive-search';
  var INPUT_SELECTOR = '.search-bar input[name="q"], [data-panda-search] input[name="q"]';
  var SEARCH_AREA_SELECTOR = '.search-bar, [data-panda-search]';
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 150;
  var PREVIEW_DELAY_MS = 120;
  var RECENT_KEY = 'panda-search-recent';
  var RECENT_MAX = 6;
  var MOBILE_QUERY = window.matchMedia('(max-width: 767.98px)');
  // Ab hier legt panda-header.css die Pille im angedockten Header links an.
  var DOCK_PILL_QUERY = window.matchMedia('(min-width: 940px)');

  var panel;
  var body;
  var input = null;
  var ghost = null;
  var cache = {};
  var controller = null;
  var debounceId = 0;
  var previewId = 0;
  var currentTerm = null;
  var activeIndex = -1;
  var completion = '';

  function searchUrl() {
    return (window.theme && theme.routes && theme.routes.search_url) || '/search';
  }

  function rootUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function suggestUrl(term) {
    var params = new URLSearchParams();
    params.set('q', term);
    params.set('section_id', SECTION_ID);
    params.set('resources[type]', 'product,collection,query');
    params.set('resources[limit]', '8');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku');
    return searchUrl() + '/suggest?' + params.toString();
  }

  function fetchSection(url, signal) {
    if (cache[url]) return Promise.resolve(cache[url]);
    return fetch(url, { signal: signal, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Search request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.getElementById('shopify-section-' + SECTION_ID);
        var content = section ? section.innerHTML : '';
        cache[url] = content;
        return content;
      });
  }

  function newRequest() {
    if (controller) controller.abort();
    controller = new AbortController();
  }

  /* ---------- Panel ---------- */

  function buildPanel() {
    panel = document.createElement('div');
    panel.className = 'ps-panel';
    panel.id = 'panda-search-panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', 'Suchvorschläge');
    panel.hidden = true;
    panel.innerHTML = '<div class="ps-panel__progress" aria-hidden="true"></div><div class="ps-panel__body"></div>';
    body = panel.querySelector('.ps-panel__body');
    document.body.appendChild(panel);

    // Fokus im Suchfeld lassen, wenn ins Panel geklickt wird.
    panel.addEventListener('mousedown', function (evt) {
      evt.preventDefault();
    });
    panel.addEventListener('click', onPanelClick);
    // mousemove statt mouseover: neu eingesetzte Inhalte unter einem ruhenden
    // Zeiger sollen keine Vorschau auslösen; Touch-Geräte bekommen keine.
    if (window.matchMedia('(hover: hover)').matches) panel.addEventListener('mousemove', onPanelHover);
  }

  function open(target) {
    if (input !== target) {
      removeGhost();
      input = target;
      input.setAttribute('aria-controls', panel.id);
      input.setAttribute('aria-autocomplete', 'list');
    }
    // Header-Section kann im Theme-Editor neu rendern; Stock-Suche erneut abschalten.
    disableStockLiveSearch();
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ps-open');
    position();
    update(true);
  }

  function close() {
    closeDockSearch();
    if (panel.hidden) return;
    panel.hidden = true;
    document.documentElement.classList.remove('ps-open');
    if (input) input.setAttribute('aria-expanded', 'false');
    clearTimeout(debounceId);
    clearTimeout(previewId);
    setCompletion('');
    setActive(-1);
  }

  function position() {
    if (!input || panel.hidden) return;
    var anchor = input.closest('.search-bar__container, [data-panda-search-anchor]') || input;
    var rect = anchor.getBoundingClientRect();
    var top = Math.round(rect.bottom + (MOBILE_QUERY.matches ? 8 : 12));
    panel.style.top = top + 'px';
    if (MOBILE_QUERY.matches) {
      panel.style.left = '';
      panel.style.width = '';
    } else {
      var width = Math.min(1000, window.innerWidth - 32);
      var left = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16));
      panel.style.left = Math.round(left) + 'px';
      panel.style.width = width + 'px';
    }
  }

  // Scrollt das Suchfeld aus dem Bild (Header-Pille oben) oder dockt der
  // Header ab, während die Pille im angedockten Header offen ist, schließt das
  // Panel, statt am Fensterrand kleben zu bleiben.
  function onScroll() {
    if (!input || panel.hidden) return;
    var rect = input.getBoundingClientRect();
    var gone = rect.bottom < 0 || rect.top > window.innerHeight || !input.offsetParent;
    var undocked = isDockSearchOpen() && !document.querySelector('.docked-header--dock');
    if (gone || undocked) {
      close();
      input.blur();
    } else {
      position();
    }
  }

  /* ---------- Suche im angedockten Header ---------- */

  // Ersetzt die Stock-Suche im angedockten Header (Feld mittig, Navigation
  // weg) durch die Pille links an der Lupe; verdeckte Menüpunkte verschwinden.
  function openDockSearch(evt) {
    var trigger = evt.target.closest && evt.target.closest('.docked-navigation-container .js-search-form-open');
    if (!trigger || !DOCK_PILL_QUERY.matches) return;
    var container = trigger.closest('.docked-navigation-container');
    var field = container.querySelector('.docking-header__utils > .search-bar input[name="q"]');
    if (!field) return;
    // Vor dem delegierten jQuery-Handler aus theme.js abfangen.
    evt.preventDefault();
    evt.stopPropagation();
    document.documentElement.classList.add('ps-dock-search');
    var pillRight = field.closest('.search-bar').getBoundingClientRect().right;
    container.querySelectorAll('.site-nav > .site-nav__item').forEach(function (item) {
      var rect = item.getBoundingClientRect();
      if (rect.width && rect.left < pillRight + 16) item.classList.add('ps-covered');
    });
    field.focus();
  }

  function isDockSearchOpen() {
    return document.documentElement.classList.contains('ps-dock-search');
  }

  function closeDockSearch() {
    if (!isDockSearchOpen()) return;
    document.documentElement.classList.remove('ps-dock-search');
    document.querySelectorAll('.ps-covered').forEach(function (item) {
      item.classList.remove('ps-covered');
    });
  }

  function render(html) {
    body.innerHTML = html;
    activeIndex = -1;
    highlight();
    renderRecent();
    refreshCompletion();
  }

  /* ---------- Suche ---------- */

  function update(immediate) {
    var term = input.value.trim();
    clearTimeout(debounceId);
    if (term.length < MIN_CHARS) {
      if (currentTerm === '') return;
      currentTerm = '';
      setCompletion('');
      load(rootUrl() + '?section_id=' + SECTION_ID, '');
      return;
    }
    if (term === currentTerm) {
      refreshCompletion();
      return;
    }
    // Vorhandene Ergänzung sofort mitführen, solange sie noch passt.
    refreshCompletion();
    debounceId = setTimeout(function () {
      currentTerm = term;
      load(suggestUrl(term), term);
    }, immediate ? 0 : DEBOUNCE_MS);
  }

  // Platzhalter im Layout des Panels, bis die erste Antwort da ist.
  function skeletonHtml() {
    var lines = '';
    for (var i = 0; i < 5; i++) lines += '<span class="ps-skeleton__line" style="width:' + (88 - i * 11) + '%"></span>';
    var cards = '';
    for (var j = 0; j < 4; j++) {
      cards += '<span class="ps-skeleton__card"><span class="ps-skeleton__media"></span>' +
        '<span class="ps-skeleton__line"></span><span class="ps-skeleton__line ps-skeleton__line--short"></span></span>';
    }
    return '<div class="ps-start ps-skeleton" aria-hidden="true">' +
      '<div class="ps-results__side"><span class="ps-skeleton__label"></span>' + lines + '</div>' +
      '<div class="ps-results__main"><span class="ps-skeleton__label"></span><div class="ps-skeleton__grid">' + cards + '</div></div>' +
      '</div>';
  }

  function load(url, term) {
    newRequest();
    if (!cache[url] && !body.firstElementChild) body.innerHTML = skeletonHtml();
    panel.classList.add('is-loading');
    fetchSection(url, controller.signal)
      .then(function (html) {
        if (term !== currentTerm) return;
        render(html);
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') console.warn('[panda-search]', err);
      })
      .then(function () {
        if (term === currentTerm) panel.classList.remove('is-loading');
      });
  }

  function preview(link) {
    var term = link.getAttribute('data-ps-term');
    var main = body.querySelector('.ps-results__main');
    if (!term || !main || main.getAttribute('data-ps-preview-term') === term) return;
    main.setAttribute('data-ps-preview-term', term);
    var request = currentTerm;
    main.classList.add('is-previewing');
    // Ohne Signal: die Vorschau soll die laufende Suche nicht abbrechen.
    fetchSection(suggestUrl(term))
      .then(function (html) {
        if (request !== currentTerm || !main.isConnected || main.getAttribute('data-ps-preview-term') !== term) return;
        var next = new DOMParser().parseFromString(html, 'text/html').querySelector('.ps-results__main');
        if (!next) return;
        var activeItem = items()[activeIndex];
        main.innerHTML = next.innerHTML;
        highlight(term);
        if (activeItem) activeIndex = items().indexOf(activeItem);
      })
      .catch(function (err) { console.warn('[panda-search]', err); })
      .then(function () {
        if (main.getAttribute('data-ps-preview-term') === term) main.classList.remove('is-previewing');
      });
  }

  function highlight(term) {
    var words = (term || currentTerm || '').toLowerCase().split(/\s+/).filter(function (w) {
      return w.length >= MIN_CHARS;
    });
    if (!words.length) return;
    var pattern = new RegExp('(' + words.map(escapeRegExp).join('|') + ')', 'gi');
    body.querySelectorAll('[data-ps-highlight]').forEach(function (el) {
      el.innerHTML = escapeHtml(el.textContent).replace(pattern, '<mark>$1</mark>');
    });
  }

  /* ---------- Autovervollständigung ---------- */

  function refreshCompletion() {
    var value = input ? input.value : '';
    var lower = value.toLowerCase();
    var match = '';
    if (value.trim().length >= MIN_CHARS && value === value.trimStart()) {
      var suggestions = body.querySelectorAll('.ps-suggestion[data-ps-preview]');
      for (var i = 0; i < suggestions.length; i++) {
        var text = suggestions[i].getAttribute('data-ps-term') || '';
        if (text.length > value.length && text.toLowerCase().indexOf(lower) === 0) {
          match = text.slice(value.length);
          break;
        }
      }
    }
    setCompletion(match);
  }

  function setCompletion(rest) {
    completion = rest;
    if (!rest || !input) {
      if (ghost) ghost.hidden = true;
      return;
    }
    if (!ghost || ghost.parentNode !== input.offsetParent) createGhost();
    var caretAtEnd = input.selectionStart === input.value.length;
    var overflowing = input.scrollWidth > input.clientWidth;
    ghost.firstChild.textContent = input.value;
    ghost.lastChild.textContent = rest;
    ghost.hidden = !caretAtEnd || overflowing;
  }

  function createGhost() {
    removeGhost();
    var style = window.getComputedStyle(input);
    ghost = document.createElement('span');
    ghost.className = 'ps-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.innerHTML = '<span class="ps-ghost__typed"></span><span class="ps-ghost__rest"></span>';
    ['fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'paddingLeft', 'paddingRight', 'textIndent'].forEach(function (prop) {
      ghost.style[prop] = style[prop];
    });
    ghost.style.left = input.offsetLeft + parseFloat(style.borderLeftWidth || 0) + 'px';
    ghost.style.top = input.offsetTop + 'px';
    ghost.style.height = input.offsetHeight + 'px';
    ghost.style.lineHeight = input.offsetHeight + 'px';
    ghost.style.width = input.clientWidth + 'px';
    if (input.offsetParent) input.offsetParent.appendChild(ghost);
  }

  function removeGhost() {
    if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
    ghost = null;
  }

  function acceptCompletion() {
    if (!completion || !ghost || ghost.hidden) return false;
    input.value += completion;
    setCompletion('');
    update(true);
    return true;
  }

  /* ---------- Tastatur & Auswahl ---------- */

  function items() {
    return Array.prototype.slice.call(body.querySelectorAll('[data-ps-item]')).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function setActive(index, fromPointer) {
    var list = items();
    list.forEach(function (el) { el.classList.remove('is-active'); });
    activeIndex = index;
    var el = list[index];
    if (!el) {
      if (input) input.removeAttribute('aria-activedescendant');
      return;
    }
    el.classList.add('is-active');
    if (fromPointer) return;
    el.scrollIntoView({ block: 'nearest' });
    if (el.hasAttribute('data-ps-preview')) preview(el);
  }

  function onKeydown(evt) {
    if (panel.hidden) return;
    var list = items();
    switch (evt.key) {
      case 'ArrowDown':
        evt.preventDefault();
        setActive(activeIndex + 1 >= list.length ? 0 : activeIndex + 1);
        break;
      case 'ArrowUp':
        evt.preventDefault();
        setActive(activeIndex - 1 < 0 ? list.length - 1 : activeIndex - 1);
        break;
      case 'ArrowRight':
      case 'Tab':
        if (!evt.shiftKey && input.selectionStart === input.value.length && acceptCompletion()) {
          evt.preventDefault();
        }
        break;
      case 'Enter':
        if (list[activeIndex]) {
          evt.preventDefault();
          list[activeIndex].click();
        }
        break;
      case 'Escape':
        evt.preventDefault();
        close();
        input.blur();
        break;
    }
  }

  function onPanelClick(evt) {
    var clear = evt.target.closest('[data-ps-recent-clear]');
    if (clear) {
      writeRecent([]);
      renderRecent();
      return;
    }
    var item = evt.target.closest('[data-ps-item]');
    if (!item) return;
    var term = item.getAttribute('data-ps-term');
    if (term) saveRecent(term);
    else if (input && input.value.trim().length >= MIN_CHARS) saveRecent(input.value.trim());
  }

  function onPanelHover(evt) {
    var link = evt.target.closest('[data-ps-preview]');
    if (!link || link.classList.contains('is-active')) return;
    setActive(items().indexOf(link), true);
    clearTimeout(previewId);
    previewId = setTimeout(function () { preview(link); }, PREVIEW_DELAY_MS);
  }

  /* ---------- Zuletzt gesucht ---------- */

  function readRecent() {
    try {
      var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) { /* privater Modus o. Ä. */ }
  }

  function saveRecent(term) {
    term = String(term).trim();
    if (term.length < MIN_CHARS) return;
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
    list.unshift(term);
    writeRecent(list.slice(0, RECENT_MAX));
  }

  function renderRecent() {
    var group = body.querySelector('[data-ps-recent]');
    if (!group) return;
    var list = readRecent();
    group.hidden = list.length === 0;
    group.querySelector('[data-ps-recent-list]').innerHTML = list.map(function (term) {
      return '<a class="ps-chip ps-chip--recent" data-ps-item data-ps-term="' + escapeHtml(term) + '" href="' +
        searchUrl() + '?q=' + encodeURIComponent(term) + '">' + escapeHtml(term) + '</a>';
    }).join('');
  }

  /* ---------- Hilfen ---------- */

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function disableStockLiveSearch() {
    document.querySelectorAll('.search-bar[data-live-search="true"]').forEach(function (bar) {
      bar.setAttribute('data-live-search', 'panda');
    });
  }

  /* ---------- Tastenkürzel „/“ ---------- */

  function isEditable(el) {
    return el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
  }

  function visibleSearchInput() {
    var fields = document.querySelectorAll(INPUT_SELECTOR);
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].offsetParent !== null && !fields[i].closest('.mobile-menu-search')) return fields[i];
    }
    return null;
  }

  function onShortcut(evt) {
    if (evt.key !== '/' || evt.ctrlKey || evt.metaKey || evt.altKey || isEditable(evt.target)) return;
    var field = visibleSearchInput();
    var opener = document.querySelector('.js-search-form-open');
    if (!field && !opener) return;
    evt.preventDefault();
    if (field) {
      field.focus();
      field.select();
    } else {
      // Mobil/zugeklappt: Stock-Button öffnet die Leiste und fokussiert das Feld.
      opener.click();
    }
  }

  // Kleiner „/“-Hinweis in der offenen Such-Pille, nur mit Maus und Tastatur.
  function addShortcutHint() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('.search-bar.settings-open-bar .search-bar__container').forEach(function (container) {
      if (container.querySelector('.ps-kbd')) return;
      var hint = document.createElement('kbd');
      hint.className = 'ps-kbd';
      hint.title = 'Taste / öffnet die Suche';
      hint.textContent = '/';
      container.appendChild(hint);
    });
  }

  /* ---------- Suchseite: ähnliche Suchen & Kategorien ---------- */

  function fillRelated() {
    var box = document.querySelector('[data-ps-related]');
    var term = box && box.getAttribute('data-ps-related');
    if (!term) return;
    var params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'query,collection');
    params.set('resources[limit]', '8');
    params.set('resources[limit_scope]', 'each');
    fetch(searchUrl() + '/suggest.json?' + params.toString(), { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        var results = data && data.resources && data.resources.results;
        if (!results) return;
        var lower = term.toLowerCase();
        var queries = (results.queries || []).filter(function (q) {
          return q.text.toLowerCase() !== lower;
        }).slice(0, 6);
        // Gleiche Regeln wie im Panel: keine Kopien, keine internen Shop-Kollektionen.
        var collections = (results.collections || []).filter(function (c) {
          return !/\(copy\)|pandaliebe/i.test(c.title);
        }).slice(0, 5);
        var html = '';
        if (collections.length) {
          html += '<div class="ps-related__row"><span class="ps-related__label">Passende Kategorien</span><div class="ps-chips">' +
            collections.map(function (c) {
              return '<a class="ps-chip ps-chip--collection" href="' + escapeHtml(c.url) + '">' + escapeHtml(c.title.replace(/ - Kollektion$/, '')) + '</a>';
            }).join('') + '</div></div>';
        }
        if (queries.length) {
          html += '<div class="ps-related__row"><span class="ps-related__label">Ähnliche Suchen</span><div class="ps-chips">' +
            queries.map(function (q) {
              return '<a class="ps-chip" href="' + escapeHtml(q.url) + '">' + escapeHtml(q.text) + '</a>';
            }).join('') + '</div></div>';
        }
        box.innerHTML = html;
        box.hidden = !html;
      })
      .catch(function (err) { console.warn('[panda-search]', err); });
  }

  function isSearchInput(el) {
    return el && el.matches && el.matches(INPUT_SELECTOR);
  }

  function init() {
    if (!window.fetch || !window.DOMParser || !window.AbortController) return;
    disableStockLiveSearch();
    buildPanel();

    document.addEventListener('focusin', function (evt) {
      if (isSearchInput(evt.target)) open(evt.target);
    });

    // Nur schließen, wenn der Fokus wirklich woandershin wandert (Tab). Fällt er
    // auf <body> zurück – Fensterwechsel, Popups von Apps –, bleibt das Panel offen.
    document.addEventListener('focusout', function (evt) {
      if (evt.target !== input) return;
      setTimeout(function () {
        var active = document.activeElement;
        if (active && active !== input && active !== document.body && !panel.contains(active)) close();
      }, 150);
    });

    document.addEventListener('pointerdown', function (evt) {
      if (panel.contains(evt.target)) return;
      if (isSearchInput(evt.target)) {
        // Feld hat noch Fokus, Panel war zu: focusin kommt nicht, also selbst öffnen.
        if (panel.hidden && document.activeElement === evt.target) open(evt.target);
        return;
      }
      if (!panel.hidden && (evt.target.closest('.js-search-form-close') || !evt.target.closest(SEARCH_AREA_SELECTOR))) close();
    });

    document.addEventListener('input', function (evt) {
      if (evt.target === input) {
        if (panel.hidden) open(input);
        else update(false);
      }
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.target === input) onKeydown(evt);
      else onShortcut(evt);
    });

    addShortcutHint();
    fillRelated();

    document.addEventListener('submit', function (evt) {
      var field = evt.target.querySelector && evt.target.querySelector(INPUT_SELECTOR);
      if (field) saveRecent(field.value);
    });

    window.addEventListener('click', openDockSearch, true);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
